// src/fleet-stats/components/SharedUI.jsx
import React, { useState, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import { C, CARD_BG, CARD_BORDER, SUBTLE_BORDER } from "../constants.js";

/* -------- button helpers -------- */
export function brandButton({ active = false } = {}) {
    return {
        backgroundColor: active ? C.strongBlue : "transparent",
        color: C.offWhite,
        border: `1px solid ${SUBTLE_BORDER}`,
        transition: "all 0.15s ease",
    };
}
export function hoverNeon(e) {
    e.currentTarget.style.borderColor = C.neonGreen;
    e.currentTarget.style.color = C.neonGreen;
}
export function unhoverNeon(e) {
    e.currentTarget.style.borderColor = SUBTLE_BORDER;
    e.currentTarget.style.color = C.offWhite;
}

/* -------- Kpi -------- */
export function Kpi({ label, value, sub = null, deltaLabel = null, deltaNegative = null }) {
    // deltaNegative: if true, a negative delta is good (e.g. error count)
    let deltaColor = C.grey2;
    if (deltaLabel) {
        const isPositive = deltaLabel.startsWith("+");
        deltaColor = (isPositive !== !!deltaNegative) ? C.neonGreen : C.red;
    }
    return (
        <div
            className="flex-1 min-w-[140px] rounded-2xl px-4 py-3"
            style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}
        >
            <div className="text-xs uppercase tracking-wide" style={{ color: C.grey2 }}>{label}</div>
            <div className="text-2xl font-semibold mt-1" style={{ color: C.offWhite }}>{value}</div>
            {(sub || deltaLabel) && (
                <div className="flex items-center gap-2 mt-1">
                    {sub && <div className="text-xs" style={{ color: C.grey2 }}>{sub}</div>}
                    {deltaLabel && (
                        <div className="text-xs font-semibold" style={{ color: deltaColor }}>
                            {deltaLabel} vs prev
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* -------- Dropdown -------- */
export function Dropdown({ label, value, options, onChange }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");

    useEffect(() => {
        const handleClickOutside = () => { setOpen(false); setQuery(""); };
        if (open) window.addEventListener("click", handleClickOutside);
        return () => window.removeEventListener("click", handleClickOutside);
    }, [open]);

    const filteredOptions = options.filter((opt) =>
        String(opt).toLowerCase().includes(query.toLowerCase())
    );

    return (
        <div className="relative w-full" onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                onClick={() => { setOpen((v) => !v); if (open) setQuery(""); }}
                className="w-full inline-flex items-stretch rounded-xl overflow-hidden text-left"
                style={{ border: "1px solid rgba(205,214,217,0.16)", backgroundColor: "#020b16" }}
            >
                <span
                    className="px-4 py-2.5 text-sm border-r select-none whitespace-nowrap"
                    style={{ backgroundColor: "rgba(255,255,255,0.02)", color: C.grey1, borderColor: "rgba(205,214,217,0.16)" }}
                >
                    {label}
                </span>
                <span className="flex-1 px-3 py-2.5 flex items-center justify-between gap-3" style={{ color: C.offWhite }}>
                    <span className="truncate">{value}</span>
                    <ChevronDown size={16} style={{ color: C.grey2, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease", flexShrink: 0 }} />
                </span>
            </button>

            {open && (
                <div
                    className="absolute left-0 right-0 mt-2 rounded-xl overflow-hidden z-50"
                    style={{ background: "linear-gradient(180deg, #182028 0%, #1d252d 100%)", border: "1px solid rgba(205,214,217,0.18)", boxShadow: "0 10px 30px rgba(0,0,0,0.45)" }}
                >
                    <div className="p-2 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                        <input
                            autoFocus
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={`Search ${label.toLowerCase()}...`}
                            className="w-full px-3 py-2 rounded-lg outline-none text-sm"
                            style={{ backgroundColor: "#020b16", color: C.offWhite, border: "1px solid rgba(205,214,217,0.16)" }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="overflow-auto" style={{ maxHeight: 320 }}>
                        {filteredOptions.length === 0 ? (
                            <div className="px-4 py-3 text-sm" style={{ color: C.grey2 }}>No matches found.</div>
                        ) : (
                            filteredOptions.map((opt) => {
                                const selected = value === opt;
                                return (
                                    <button
                                        key={opt}
                                        type="button"
                                        onClick={() => { onChange(opt); setOpen(false); setQuery(""); }}
                                        className="w-full px-4 py-2.5 text-left flex items-center justify-between transition-colors"
                                        style={{ backgroundColor: selected ? "rgba(255,255,255,0.06)" : "transparent", color: C.offWhite, borderTop: "1px solid rgba(255,255,255,0.03)" }}
                                        onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)"; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selected ? "rgba(255,255,255,0.06)" : "transparent"; }}
                                    >
                                        <span>{opt}</span>
                                        {selected && <Check size={16} style={{ color: C.offWhite }} />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* -------- SectionHeader -------- */
export function SectionHeader({ icon: Icon, children, right = null }) {
    return (
        <div className="flex items-center justify-between mb-3 gap-3">
            <div className="flex items-center gap-2">
                <Icon size={18} style={{ color: C.grey2 }} />
                <h2 className="text-lg font-semibold" style={{ color: C.offWhite }}>{children}</h2>
            </div>
            {right}
        </div>
    );
}

/* -------- QuickRangeButton -------- */
export function QuickRangeButton({ label, onClick, active = false }) {
    return (
        <button
            onClick={onClick}
            className="px-3 h-8 rounded-lg text-sm font-semibold transition-colors"
            style={brandButton({ active })}
            onMouseEnter={hoverNeon}
            onMouseLeave={unhoverNeon}
        >
            {label}
        </button>
    );
}

/* -------- DataTable -------- */
export function DataTable({ columns, rows, emptyMessage = "No data.", maxVisibleRows = 10, rowHeight = 44, onRowClick = null }) {
    const hasRows = rows.length > 0;
    const maxHeight = hasRows ? maxVisibleRows * rowHeight + 44 : "auto";

    return (
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
            <div className="overflow-auto" style={{ maxHeight }}>
                <table className="w-full text-sm">
                    <thead style={{ backgroundColor: C.darkBlue }}>
                        <tr style={{ color: C.grey2 }}>
                            {columns.map((col) => (
                                <th key={col.key} className={`px-3 py-2 font-medium ${col.align === "right" ? "text-right" : "text-left"}`}>
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {!hasRows ? (
                            <tr>
                                <td colSpan={columns.length} className="px-3 py-4" style={{ color: C.grey2 }}>
                                    {emptyMessage}
                                </td>
                            </tr>
                        ) : (
                            rows.map((row, i) => (
                                <tr
                                    key={row._key || i}
                                    style={{ borderTop: "1px solid rgba(255,255,255,0.06)", cursor: onRowClick ? "pointer" : undefined }}
                                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                                    onMouseEnter={onRowClick ? (e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)") : undefined}
                                    onMouseLeave={onRowClick ? (e) => (e.currentTarget.style.backgroundColor = "transparent") : undefined}
                                >
                                    {columns.map((col) => (
                                        <td key={col.key} className={`px-3 py-2 ${col.align === "right" ? "text-right tabular-nums" : ""}`} style={{ color: C.offWhite }}>
                                            {col.render ? col.render(row) : row[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}